import {
	ICursorListResponse,
	useXHR,
	type XHRInstance,
} from "@sembark-travel/xhr"
import useSWR, { type SWRConfiguration } from "swr"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { IUser, useAuthUser } from "../Auth"
import { usePrivateChannel } from "../Websockets"
import type { TWATemplate } from "../Integrations"
import type { TWAQuickSendResult } from "../WhatsAppQuickSends"
import { ITrip } from "../Trips"
import { ITripPlanRequest } from "../Leads"

type TWhatsAppMessageStatus =
	| "accepted"
	| "sent"
	| "delivered"
	| "read"
	| "failed"
	| "received"
	| "system"

export type TWhatsAppMessageDirection = "inbound" | "outbound" | "system"

/** One delivery transition, with Meta's raw payload for that step. */
export type TWhatsAppMessageStatusEntry = {
	status: TWhatsAppMessageStatus
	created_at: string
	payload?: {
		errors?: Array<Record<string, unknown>>
	}
}

export type TWhatsAppMessage = {
	id: number
	/**
	 * Meta's id for the message (`wamid.…`). A media send still on its way
	 * carries a `pending:` placeholder until Meta accepts it.
	 */
	message_id: string
	from_number?: string | null
	from_name?: string
	to_number?: string | null
	to_name?: string
	/**
	 * Where the message was actually delivered when the integration's test
	 * mode redirected it away from `to_number`. Null otherwise.
	 */
	test_recipient?: string | null
	from_bsuid?: string | null
	from_parent_bsuid?: string | null
	direction?: TWhatsAppMessageDirection
	message?: string
	/**
	 * One line saying what the message is, for rows that show a message
	 * without rendering it: the text, or the kind of media and its caption,
	 * or the template it was sent from.
	 */
	preview?: string
	media_type?: string | null
	rendered_html?: string
	template?: TWATemplate
	created_by?: IUser
	created_at: string
	status: TWhatsAppMessageStatus
	/**
	 * Null when sent or received through Sembark. `business_app` when sent
	 * from the WhatsApp Business app on the same number; `history` when
	 * imported from the app's past chats.
	 */
	sent_via?: "business_app" | "history" | null
	/** When the business last edited it in the WhatsApp Business app. */
	edited_at?: string | null
	/** The texts it had before each edit, oldest first. */
	edit_history?: Array<{ message: string | null; replaced_at: string }> | null
	/**
	 * When the business deleted it for everyone in the WhatsApp Business app.
	 * Its contents are kept; the customer no longer sees them.
	 */
	revoked_at?: string | null
	/** Null until a status webhook reports how Meta billed the message. */
	pricing_billable?: boolean | null
	pricing_category?:
		| "service"
		| "marketing"
		| "marketing_lite"
		| "utility"
		| "authentication"
		| "authentication_international"
		| null
	pricing_model?:
		| "PMP" // Per-Message Pricing
		| "CBP" // Conversation-Based Pricing
		| null
	pricing_type?: "regular" | "free_customer_service" | "free_entry_point" | null
	statuses?: Array<TWhatsAppMessageStatusEntry>
	/** The value behind a tapped button. `message` holds the label. */
	interaction_payload?: string | null
	/** Answers from a submitted WhatsApp Flow; the body renders them. */
	flow_response?: Record<string, unknown> | null
	context_message_id?: string | null
	/** The message a quick reply answers, quoted above the bubble. */
	context_message?: {
		id: number
		message_id: string
		message?: string | null
		/** Server-rendered, like the bubble's own body. Same opt-in. */
		rendered_html?: string
		media_type?: string | null
		direction?: TWhatsAppMessageDirection
	} | null
	/**
	 * Diagnostics, returned only by the message details endpoint: what Meta
	 * sent us for an inbound message, and what we sent Meta for an outbound
	 * one. Shapes are the provider's, so they stay unknown rather than
	 * pretending to a structure that can change under us.
	 */
	raw_payload?: Record<string, unknown> | null
	request_payload?: Record<string, unknown> | null
	/** Meta's reason for a failure, lifted out of the status payloads. */
	error?: {
		code?: number | null
		title?: string | null
		details?: string | null
	} | null
	/** Emoji reactions, keyed by the reacting number. */
	reactions?: Record<
		string,
		{ emoji: string; name?: string | null; reacted_at?: string }
	> | null
}

// The per-user read/subscription fields the thread endpoints echo back.
export type TWhatsAppThreadReadState = {
	id: number
	unread_count?: number
	is_subscribed?: boolean
	last_read_at?: string | null
}

// The customer as one sender number knows them.
export type TWhatsAppContact = {
	id: number
	contact_number?: string | null
	/** The name the business saved in the WhatsApp Business app, else the profile name */
	name?: string | null
	profile_name?: string | null
	address_book_name?: string | null
	bsuid?: string | null
	parent_bsuid?: string | null
	username?: string | null
	/** Set once the customer's WhatsApp identity changed; the thread is sealed */
	identity_changed_at?: string | null
}

// A WhatsApp conversation thread as returned by the inbox list/detail and the
// trip overview. `messages` is only present on the detail payload.
export type TWhatsAppThread = {
	id: number
	wa_integrated_app_id: number
	contact?: TWhatsAppContact
	/**
	 * Where the conversation came from when Meta said: the Click-to-WhatsApp
	 * ad or post behind its first message. Set once, never overwritten.
	 */
	referral?: {
		source_type?: "ad" | "post" | string
		source_id?: string
		source_url?: string
		headline?: string
		body?: string
		media_type?: string
		ctwa_clid?: string
	} | null
	latest_message_at?: string | null
	last_inbound_message_at?: string | null
	last_outbound_message_at?: string | null
	integrated_app?: {
		id: number
		integration_name?: string | null
		provider: string
	}
	phone_number?: {
		id: number
		display_phone_number: string
		name?: string | null
		wa_business_account?: {
			id: number
			name?: string | null
		} | null
	}
	// Every record this thread is attached to, whatever its type. `label` is
	// the record's reference id; `data` carries the type's own resource and is
	// present only on the thread detail, so list rows stay lean.
	links?: TWhatsAppThreadLink[]
	messages_count?: number
	messages?: TWhatsAppMessage[]
	latest_message?: TWhatsAppMessage
	// Per-user read/subscription state for the authenticated operator.
	unread_count?: number
	is_subscribed?: boolean
	last_read_at?: string | null
	last_read_message_id?: number | null
	// Detail-only (thread `show`): powers the inbox right-context pane.
	members?: Array<{ id: number; name?: string | null }>
}

// One linked record. `type` is the model's morph key and decides which shape
// `data` holds; it comes off the wire as a plain string, so the consumer
// narrows it.
export type TWhatsAppThreadLink = {
	id: number
	label: string
} & (
	| {
			type: "trips"
			data?: ITrip
	  }
	| {
			type: "trip_plan_requests"
			data?: ITripPlanRequest
	  }
)

// Cursor page of inbox threads. The inbox never needs a total, so the list
// endpoint is called with `pagination: "cursor"` and returns next/prev cursors.
export async function fetchWhatsAppInboxThreads(
	xhr: XHRInstance,
	params: {
		q?: string
		integrated_app_id?: number
		model_type?: string
		model_id?: number
		limit?: number
		cursor?: string
	}
): Promise<ICursorListResponse<TWhatsAppThread>> {
	return xhr
		.get("/wa-threads", {
			params: { ...params, pagination: "cursor" },
		})
		.then((resp) => resp.data)
}

// Per-user read/subscription/reply actions for a WhatsApp thread. Thread ids
// are globally unique, so these carry no integrated-app id.
export function WhatsAppThreadXHR(xhr: XHRInstance) {
	return {
		async markRead(
			threadId: number,
			messageId?: number
		): Promise<TWhatsAppThreadReadState> {
			return xhr
				.patch(
					`/wa-threads/${threadId}/read`,
					messageId ? { message_id: messageId } : {}
				)
				.then((resp) => resp.data.data)
		},
		async subscribe(threadId: number): Promise<TWhatsAppThreadReadState> {
			return xhr
				.post(`/wa-threads/${threadId}/subscribe`)
				.then((resp) => resp.data.data)
		},
		async unsubscribe(threadId: number): Promise<TWhatsAppThreadReadState> {
			return xhr
				.delete(`/wa-threads/${threadId}/subscribe`)
				.then((resp) => resp.data.data)
		},
		// Reply to the conversation. `payload` is a plain object (text reply) or
		// FormData (attachment); both go to the same endpoint.
		async send(
			threadId: number,
			payload: FormData | Record<string, unknown>
		): Promise<void> {
			return xhr
				.post(`/wa-threads/${threadId}/messages`, payload)
				.then(() => undefined)
		},
		// Send every message of a quick send, in order. Answers how far it got.
		async sendQuickSend(
			threadId: number,
			quickSendId: number
		): Promise<TWAQuickSendResult> {
			return xhr
				.post<TWAQuickSendResult>(`/wa-threads/${threadId}/messages`, {
					wa_quick_send_id: quickSendId,
				})
				.then((resp) => resp.data)
		},
		// Heartbeat: pass a thread id while a conversation is open (suppresses
		// its notifications), or nothing while merely browsing the inbox.
		async presence(threadId?: number): Promise<void> {
			return xhr
				.post(`/wa-threads/presence`, {
					...(threadId ? { thread_id: threadId } : {}),
				})
				.then(() => undefined)
		},
	}
}

// The inbox conversation detail — a single thread with its full message history.
export function useFetchWhatsAppInboxThread(
	threadId: number | string | null | undefined,
	config?: SWRConfiguration
) {
	const xhr = useXHR()
	const cacheKey = threadId
		? `/wa-threads/${threadId}?include=rendered_html`
		: null
	const { data, error, isValidating, mutate } = useSWR(
		cacheKey,
		() =>
			xhr
				.get<{
					data: TWhatsAppThread
				}>(`/wa-threads/${threadId}`, {
					params: { include: "rendered_html" },
				})
				.then((resp) => resp.data.data),
		{ revalidateOnFocus: false, ...config }
	)

	return {
		thread: data,
		error,
		isLoading: !data && !error,
		isRefreshing: isValidating,
		refresh: mutate,
	}
}

// --- Message pagination + incremental realtime ---

// Broadcast payloads on the `wa.thread.{id}` channel.
export type TWhatsAppThreadActivityPayload = {
	thread_id: number
	message_id: number
	latest_message_at?: string | null
}

export type TWhatsAppMessageStatusPayload = {
	thread_id: number
	message_id: number
	status: TWhatsAppMessage["status"]
	statuses?: TWhatsAppMessage["statuses"]
}

export type TWhatsAppMessageReactionPayload = {
	thread_id: number
	message_id: number
	reactions: NonNullable<TWhatsAppMessage["reactions"]>
}

/**
 * A message's contents changed after it was stored: edited or deleted in the
 * WhatsApp Business app.
 */
export type TWhatsAppMessageUpdatedPayload = {
	thread_id: number
	message_id: number
	message: string | null
	rendered_html: string
	edited_at: string | null
	edit_history: TWhatsAppMessage["edit_history"]
	revoked_at: string | null
}

// Paginated per-thread message access. `list` is a newest-first cursor page
// (walk `meta.next_cursor` to load older); `delta` pulls only the messages newer
// than `afterId` — a cheap catch-up after a realtime event or a send.
export function WhatsAppThreadMessagesXHR(xhr: XHRInstance) {
	return {
		async list(
			threadId: number,
			params: { cursor?: string; limit?: number } = {}
		): Promise<ICursorListResponse<TWhatsAppMessage>> {
			return xhr
				.get(`/wa-threads/${threadId}/messages`, {
					params: { include: "rendered_html", ...params },
				})
				.then((resp) => resp.data)
		},
		async delta(
			threadId: number,
			afterId: number
		): Promise<TWhatsAppMessage[]> {
			return xhr
				.get(`/wa-threads/${threadId}/messages`, {
					params: { after_id: afterId, include: "rendered_html" },
				})
				.then((resp) => resp.data.data)
		},
	}
}

// Upsert `incoming` over `existing` by id, ordered oldest→newest (the render
// order). Used to append new messages and prepend older pages without duplicates.
export function mergeMessages(
	existing: TWhatsAppMessage[],
	incoming: TWhatsAppMessage[]
): TWhatsAppMessage[] {
	const byId = new Map<number, TWhatsAppMessage>()
	for (const message of existing) {
		byId.set(message.id, message)
	}
	for (const message of incoming) {
		byId.set(message.id, message)
	}
	// By when each was sent, then id. History imported from the WhatsApp
	// Business app is stored after newer messages, so ids alone do not follow
	// time.
	return Array.from(byId.values()).sort(
		(a, b) => Date.parse(a.created_at) - Date.parse(b.created_at) || a.id - b.id
	)
}

/**
 * The highest id held, which the delta pull asks past. Not the last message
 * shown: messages are ordered by time, and an imported one can have a higher
 * id than newer ones.
 */
function maxMessageId(messages: TWhatsAppMessage[], fallback = 0): number {
	return messages.reduce((max, message) => Math.max(max, message.id), fallback)
}

const MESSAGES_PAGE_SIZE = 30

/**
 * Owns a thread's message list: loads the newest page, pages older on demand,
 * and keeps itself live over websockets — a new-message broadcast pulls only the
 * delta, a status broadcast patches that message in place. No full-thread
 * refetch, and only the newest page is loaded up front. Shared by the inbox and
 * the trip-page conversation.
 */
export function useThreadMessages(
	threadId: number | null | undefined,
	// Fired on a new-message activity broadcast, after the delta pull — lets the
	// caller refresh its own thread metadata (the context pane, the thread list
	// preview / ordering) off the same single subscription.
	// `onActivity` fires on any new-message broadcast (after the delta pull);
	// `onInbound` fires only when that delta brought a new *inbound* message, so
	// the caller can play a notification without re-deriving direction.
	// `onReaction` fires when a contact reacts to a message already on screen --
	// separate because it is not a new message and never moves the delta.
	options: {
		onActivity?: () => void
		onInbound?: () => void
		onReaction?: () => void
	} = {}
) {
	const { onActivity, onInbound, onReaction } = options
	const xhr = useXHR()
	const api = useMemo(() => WhatsAppThreadMessagesXHR(xhr), [xhr])
	// Keep the latest callbacks without re-subscribing the channel.
	const onActivityRef = useRef(onActivity)
	useEffect(() => {
		onActivityRef.current = onActivity
	}, [onActivity])
	const onInboundRef = useRef(onInbound)
	useEffect(() => {
		onInboundRef.current = onInbound
	}, [onInbound])
	const onReactionRef = useRef(onReaction)
	useEffect(() => {
		onReactionRef.current = onReaction
	}, [onReaction])
	const [messages, setMessages] = useState<TWhatsAppMessage[]>([])
	// Mirrors `messages` for reads outside render. A state updater runs later
	// than the call that schedules it, so anything needing the current value
	// synchronously -- deciding whether a reaction was added or removed --
	// cannot read it from inside one.
	const messagesRef = useRef<TWhatsAppMessage[]>([])
	useEffect(() => {
		messagesRef.current = messages
	}, [messages])
	const [olderCursor, setOlderCursor] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isLoadingOlder, setIsLoadingOlder] = useState(false)
	const loadingOlderRef = useRef(false)
	// The newest message id we hold — the `after_id` anchor for delta pulls.
	const maxIdRef = useRef(0)

	// Load the newest page whenever the thread changes.
	useEffect(() => {
		if (!threadId) {
			setMessages([])
			setOlderCursor(null)
			maxIdRef.current = 0
			return
		}
		let cancelled = false
		setIsLoading(true)
		setMessages([])
		setOlderCursor(null)
		maxIdRef.current = 0
		api
			.list(threadId, { limit: MESSAGES_PAGE_SIZE })
			.then((res) => {
				if (cancelled) {
					return
				}
				// The endpoint is newest-first; reverse to oldest→newest for display.
				const ascending = [...res.data].reverse()
				setMessages(ascending)
				maxIdRef.current = maxMessageId(ascending)
				setOlderCursor(res.meta.next_cursor)
			})
			.catch(() => undefined)
			.finally(() => {
				if (!cancelled) {
					setIsLoading(false)
				}
			})
		return () => {
			cancelled = true
		}
	}, [api, threadId])

	const loadOlder = useCallback(async () => {
		if (!threadId || !olderCursor || loadingOlderRef.current) {
			return
		}
		loadingOlderRef.current = true
		setIsLoadingOlder(true)
		try {
			const res = await api.list(threadId, { cursor: olderCursor })
			const ascending = [...res.data].reverse()
			setMessages((prev) => mergeMessages(ascending, prev))
			setOlderCursor(res.meta.next_cursor)
		} catch {
			// A failed older-page pull is non-fatal — the button stays available.
		} finally {
			loadingOlderRef.current = false
			setIsLoadingOlder(false)
		}
	}, [api, threadId, olderCursor])

	// Pull only the messages newer than the newest we hold, and merge them in.
	//
	// A send triggers this twice — once directly (so the sent message shows even
	// with realtime off) and once from the outbound activity broadcast the send
	// emits (which also lets OTHER agents on the thread sync it). The two delta
	// calls run one after another, not concurrently, so an in-flight guard can't
	// collapse them; both are cheap and id-idempotent, so the small redundancy is
	// accepted rather than dropping the broadcast that multi-agent sync needs.
	const syncNew = useCallback(async () => {
		if (!threadId) {
			return
		}
		try {
			const rows = await api.delta(threadId, maxIdRef.current)
			if (!rows.length) {
				return
			}
			const previousMaxId = maxIdRef.current
			// Imported history is past conversation, not something to cue.
			const hasNewInbound = rows.some(
				(row) =>
					row.id > previousMaxId &&
					row.direction === "inbound" &&
					row.sent_via !== "history"
			)
			setMessages((prev) => {
				const merged = mergeMessages(prev, rows)
				maxIdRef.current = maxMessageId(merged, maxIdRef.current)
				return merged
			})
			if (hasNewInbound) {
				onInboundRef.current?.()
			}
		} catch {
			// Ignore — the next event or a manual refresh will reconcile.
		}
	}, [api, threadId])

	// Realtime: a new-message activity triggers a delta pull; status,
	// reaction and content changes patch that message in place. They cannot
	// go through the delta pull -- that only fetches messages newer than the
	// newest one held, and these change a message the client already has.
	usePrivateChannel<
		| TWhatsAppThreadActivityPayload
		| TWhatsAppMessageStatusPayload
		| TWhatsAppMessageReactionPayload
		| TWhatsAppMessageUpdatedPayload
	>(
		threadId ? `wa.thread.${threadId}` : null,
		[
			".wa.thread.activity",
			".wa.message.status",
			".wa.message.reaction",
			".wa.message.updated",
		],
		(payload) => {
			if ("rendered_html" in payload) {
				setMessages((prev) =>
					prev.map((message) =>
						message.id === payload.message_id
							? {
									...message,
									message: payload.message ?? undefined,
									rendered_html: payload.rendered_html,
									edited_at: payload.edited_at,
									edit_history: payload.edit_history,
									revoked_at: payload.revoked_at,
								}
							: message
					)
				)
				return
			}
			if ("reactions" in payload) {
				// A removal is not worth a cue, so compare counts rather than
				// firing on any change.
				const before = messagesRef.current.find(
					(message) => message.id === payload.message_id
				)
				const added =
					Object.keys(payload.reactions).length >
					Object.keys(before?.reactions || {}).length

				setMessages((prev) =>
					prev.map((message) =>
						message.id === payload.message_id
							? { ...message, reactions: payload.reactions }
							: message
					)
				)

				if (added) {
					onReactionRef.current?.()
				}
				return
			}
			if ("status" in payload) {
				setMessages((prev) =>
					prev.map((message) =>
						message.id === payload.message_id
							? {
									...message,
									status: payload.status,
									statuses: payload.statuses ?? message.statuses,
								}
							: message
					)
				)
				return
			}
			syncNew()
			// Let the caller refresh its thread metadata (context pane, list row).
			onActivityRef.current?.()
		}
	)

	return {
		messages,
		loadOlder,
		hasMoreOlder: Boolean(olderCursor),
		isLoading,
		isLoadingOlder,
		syncNew,
	}
}

/**
 * Whether this tenant can send on WhatsApp at all. The WhatsApp analogue of
 * the mailbox check that gates the email send, and the gate for every
 * WhatsApp surface: without it a tenant that never connected WhatsApp is
 * shown panels asking them to connect one.
 *
 * Lives here rather than beside a feature component so the surfaces that only
 * need the capability do not pull a whole conversation view in with it.
 */
export function useCanSendOnWhatsApp(): boolean {
	const { user } = useAuthUser()

	return (user?.outbound_whatsapp_numbers_count || 0) > 0
}
